/**
 * Query Keyファクトリー関数
 * 階層的なQuery Keyを生成し、キャッシュの管理を容易にする
 */

/**
 * アカウント関連のQuery Key
 */
export const accountKeys = {
  // アカウント一覧（検索パラメータ付き）
  list: (search?: string) => ['accounts', 'list', search] as const,
  // アカウント詳細
  detail: (id: string) => ['accounts', 'detail', id] as const,
  // 全てのアカウント関連Query
  all: () => ['accounts'] as const,
} as const;

/**
 * お気に入り関連のQuery Key
 */
export const favoriteKeys = {
  // お気に入りアカウントID一覧
  accountIds: () => ['favorites', 'accounts'] as const,
  // 全てのお気に入り関連Query
  all: () => ['favorites'] as const,
} as const;

/**
 * 倉庫関連のQuery Key
 */
export const warehouseKeys = {
  // 倉庫一覧
  list: (accountCode: string) => ['warehouses', 'list', accountCode] as const,
  // 全ての倉庫関連Query
  all: () => ['warehouses'] as const,
} as const;

/**
 * 店舗関連のQuery Key
 */
export const sellerKeys = {
  // 店舗一覧
  list: (accountCode: string) => ['sellers', 'list', accountCode] as const,
  // 全ての店舗関連Query
  all: () => ['sellers'] as const,
} as const;

/**
 * 店舗グループ関連のQuery Key
 */
export const sellerGroupKeys = {
  // 店舗グループ一覧
  list: (accountCode: string) => ['sellerGroups', 'list', accountCode] as const,
  // 全ての店舗グループ関連Query
  all: () => ['sellerGroups'] as const,
} as const;

/**
 * 店舗階層関連のQuery Key（FKV3-12590）
 */
export const sellerHierarchyKeys = {
  // 店舗階層一覧
  list: (accountCode: string) => ['sellerHierarchies', 'list', accountCode] as const,
  // 全ての店舗階層関連Query
  all: () => ['sellerHierarchies'] as const,
} as const;

/**
 * 店舗タグ関連のQuery Key（FKV3-13002）
 */
export const sellerTagKeys = {
  // タグ種別一覧（配下のタグ名込み）
  types: (accountCode: string) => ['sellerTags', 'types', accountCode] as const,
  // 店舗へのタグ付与
  assignments: (accountCode: string) => ['sellerTags', 'assignments', accountCode] as const,
  // 全ての店舗タグ関連Query
  all: () => ['sellerTags'] as const,
} as const;

/**
 * 商品グループ関連のQuery Key
 */
export const productGroupKeys = {
  // 商品グループ階層一覧
  list: (accountCode: string) => ['productGroups', 'list', accountCode] as const,
  // 拡張版商品グループ階層一覧
  extended: (accountCode: string) => ['productGroups', 'extended', accountCode] as const,
  // 全ての商品グループ関連Query
  all: () => ['productGroups'] as const,
} as const;

/**
 * 新商品関連のQuery Key
 */
export const newProductKeys = {
  // 新商品一覧（baseDateを含める）
  list: (accountCode: string, baseDate: string) =>
    ['newProducts', 'list', accountCode, baseDate] as const,
  // 全ての新商品関連Query
  all: () => ['newProducts'] as const,
} as const;

/**
 * 物流出荷KPI関連のQuery Key
 */
export const logisticsShippingKpiKeys = {
  // ジョブ一覧
  jobs: (accountId: string, executedBy?: string) =>
    ['logisticsShippingKpi', 'jobs', accountId, executedBy] as const,
  // ジョブ詳細
  jobDetail: (accountId: string, jobId: string) =>
    ['logisticsShippingKpi', 'jobDetail', accountId, jobId] as const,
  // 全ての物流出荷KPI関連Query
  all: () => ['logisticsShippingKpi'] as const,
} as const;

/**
 * 企業関連のQuery Key
 */
export const companyKeys = {
  // 企業一覧（検索パラメータ付き）
  list: (search?: string) => ['mise', 'companies', 'list', search] as const,
  // 企業詳細
  detail: (id: string) => ['mise', 'companies', 'detail', id] as const,
  // 企業に紐づくアカウント一覧
  accounts: (companyId: string) => ['mise', 'companies', companyId, 'accounts'] as const,
  // 企業に紐づくユーザー一覧
  users: (companyId: string) => ['mise', 'companies', companyId, 'users'] as const,
  // 企業のメールテンプレート
  emailTemplates: (companyId: string) =>
    ['mise', 'companies', companyId, 'email-templates'] as const,
  // 全ての企業関連Query
  all: () => ['mise', 'companies'] as const,
} as const;

/**
 * アカウント（mise）関連のQuery Key
 */
export const miseAccountKeys = {
  // アカウント詳細
  detail: (id: string) => ['miseAccounts', 'detail', id] as const,
  // テーブルレコード件数
  tableRecordCounts: (id: string) => ['miseAccounts', 'tableRecordCounts', id] as const,
  // バッチタスク一覧
  tasks: (id: string) => ['miseAccounts', 'tasks', id] as const,
  // アカウント機能設定
  features: (id: string) => ['miseAccounts', 'features', id] as const,
  // 集計期間選択肢
  aggregationPeriods: (id: string) => ['miseAccounts', 'aggregationPeriods', id] as const,
  // アカウント設定 next（予約中のバッチ集計条件）
  settingsNext: (id: string) => ['miseAccounts', 'settingsNext', id] as const,
  // 第1階層の商品グループ一覧
  firstLevelProductGroups: (id: string) => ['miseAccounts', 'firstLevelProductGroups', id] as const,
  // 全てのアカウント（mise）関連Query
  all: () => ['miseAccounts'] as const,
} as const;

/**
 * 店舗（mise）関連のQuery Key
 */
export const miseSellerKeys = {
  // 集計後の店舗一覧
  current: (accountId: string) => ['miseSellers', 'current', accountId] as const,
  // 集計前の店舗一覧
  next: (accountId: string) => ['miseSellers', 'next', accountId] as const,
  // 店舗一覧（snapshot_hot.sellers）
  list: (accountId: string) => ['miseSellers', 'list', accountId] as const,
  // 全ての店舗（mise）関連Query
  all: () => ['miseSellers'] as const,
} as const;

/**
 * mise利用状況関連のQuery Key
 */
export const usageKeys = {
  // 利用状況データ（日付範囲・企業ID付き）
  data: (startDate: string, endDate: string, companyId?: string) =>
    ['mise', 'usage', startDate, endDate, companyId ?? ''] as const,
  // 全てのmise利用状況関連Query
  all: () => ['mise', 'usage'] as const,
} as const;

/**
 * LLMコスト関連のQuery Key
 */
export const llmCostKeys = {
  // LLMコストデータ（日付範囲・アカウントコード付き）
  data: (startDate: string, endDate: string, accountCode?: string) =>
    ['mise', 'llm-cost', startDate, endDate, accountCode ?? ''] as const,
  // 全てのLLMコスト関連Query
  all: () => ['mise', 'llm-cost'] as const,
} as const;

/**
 * 店舗グループ（mise）関連のQuery Key
 */
export const miseSellerGroupKeys = {
  // 店舗グループ一覧（snapshot_hot.seller_groups）
  list: (accountId: string) => ['miseSellerGroups', 'list', accountId] as const,
  // 全ての店舗グループ（mise）関連Query
  all: () => ['miseSellerGroups'] as const,
} as const;

/**
 * 店舗グループ current/next（mise）関連のQuery Key
 */
export const miseSellerGroupCurrentNextKeys = {
  current: (accountId: string) => ['miseSellerGroupsCN', 'current', accountId] as const,
  next: (accountId: string) => ['miseSellerGroupsCN', 'next', accountId] as const,
  all: () => ['miseSellerGroupsCN'] as const,
} as const;

/**
 * 店舗-店舗グループマッピング（mise）関連のQuery Key
 */
export const miseSellerSellerGroupMapKeys = {
  current: (accountId: string) => ['miseSellerSellerGroupMap', 'current', accountId] as const,
  next: (accountId: string) => ['miseSellerSellerGroupMap', 'next', accountId] as const,
  all: () => ['miseSellerSellerGroupMap'] as const,
} as const;

/**
 * 商品グループタイプ（mise）関連のQuery Key
 */
export const miseProductGroupTypeKeys = {
  // 商品グループタイプ一覧（snapshot_hot.product_group_types）
  list: (accountId: string) => ['miseProductGroupTypes', 'list', accountId] as const,
  // 全ての商品グループタイプ（mise）関連Query
  all: () => ['miseProductGroupTypes'] as const,
} as const;

/**
 * 問い合わせ管理関連のQuery Key
 */
export const inquiryKeys = {
  // 問い合わせ一覧（フィルタパラメータ付き）
  list: (params: Record<string, unknown>) => ['mise', 'inquiries', 'list', params] as const,
  // 問い合わせ詳細
  detail: (id: string) => ['mise', 'inquiries', 'detail', id] as const,
  // 全ての問い合わせ関連Query
  all: () => ['mise', 'inquiries'] as const,
} as const;

/**
 * 予測誤差評価関連のQuery Key
 */
export const predictionErrorEvaluationKeys = {
  // 全アカウントのジョブ一覧
  allJobs: () => ['predictionErrorEvaluation', 'allJobs'] as const,
  // アカウント別ジョブ一覧
  jobs: (accountCode: string) => ['predictionErrorEvaluation', 'jobs', accountCode] as const,
  // ジョブ詳細
  jobDetail: (accountCode: string, jobId: string) =>
    ['predictionErrorEvaluation', 'jobDetail', accountCode, jobId] as const,
  // 全ての予測誤差評価関連Query
  all: () => ['predictionErrorEvaluation'] as const,
} as const;

/**
 * custom-dashboard Agent の LLM コスト・運用確認ビュー（T-0042）のQuery Key
 */
export const dashboardLlmCostKeys = {
  // 全フィルタ条件付き
  data: (
    startDate: string,
    endDate: string,
    granularity: string,
    accountCode: string,
    tabId: string,
    status: string,
  ) =>
    ['dashboard', 'llm-cost', startDate, endDate, granularity, accountCode, tabId, status] as const,
  all: () => ['dashboard', 'llm-cost'] as const,
} as const;

/**
 * MCP 利用状況関連のQuery Key
 */
export const mcpUsageKeys = {
  // MCP 利用状況データ（全フィルタ条件付き）
  data: (
    startDate: string,
    endDate: string,
    callerType: string,
    companyId: string,
    accountCode: string,
    principal: string,
    granularity: string,
  ) =>
    [
      'ai',
      'mcp-usage',
      startDate,
      endDate,
      callerType,
      companyId,
      accountCode,
      principal,
      granularity,
    ] as const,
  // 全ての MCP 利用状況関連Query
  all: () => ['ai', 'mcp-usage'] as const,
} as const;

/**
 * 使用量集計参照関連のQuery Key
 */
export const usageAggregationKeys = {
  // 使用量集計データ（期間・集計レベル別）
  data: (from: string, to: string, level: string) =>
    ['usage-aggregation', from, to, level] as const,
  // アカウント別の日次一覧（期間別）
  daily: (account: string, from: string, to: string) =>
    ['usage-aggregation', 'daily', account, from, to] as const,
  // 全ての使用量集計関連Query
  all: () => ['usage-aggregation'] as const,
} as const;
